import { useEffect } from 'react';
import { useDispatch, useSelector } from 'src/store/store';
import { fetchPost, fetchPrivatePost } from 'src/modules/community/singlePost/actions/fetchActions';
import { showSecondPageBlockMessageBox } from 'src/modules/community/singlePost/actions/actions';
import { CommunityLayout } from 'src/components/layouts';
import SinglePostSidebar from 'src/modules/community/shared/components/sidebar/singlePostSidebar';
import PostContent from 'src/modules/community/singlePost/components/postContent';
import { useTopConditionModal } from 'src/modules/profileSurvey/components/communityTopConditionsModal';
import { useIsCommunityMember } from 'src/utils/context';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { useUpdateEffect } from 'src/utils/hooks';
import { getPostSeo } from 'src/utils/seo';
import { Key, getLocalStorageItem, setLocalStorageItem } from 'src/utils/localStorage';
import { GoogleAds } from 'src/modules/shared/modal/components/googleAdsModal';

export default function SinglePostPage(): JSX.Element {
  const { post } = useSelector(({ pages }) => pages.singlePost);
  const { currentCommunity } = useSelector(({ context }) => context);

  useFetchPost();
  usePostFootfall();
  useSecondPageBlock();
  useTopConditionModal();

  if (!post || !currentCommunity) return null;

  return (
    <CommunityLayout
      currentPage="post"
      seo={post && currentCommunity ? getPostSeo(post, currentCommunity) : {}}
      sideBarContent={<SinglePostSidebar />}
    >
      <PostContent />
      <GoogleAds pageType="post" />
    </CommunityLayout>
  );
}

const usePostFootfall = () => {
  const postId = useSelector(({ pages }) => pages.singlePost.post?.id);
  useEffect(() => {
    if (!postId) return;
    trackFootfallEvent(Event.VIEWED_POST, { postId });
  }, [postId]);
};

const useFetchPost = () => {
  const dispatch = useDispatch();
  const id = useSelector(({ pages }) => pages.singlePost.post?.id);
  const slug = useSelector(({ context }) => context.currentCommunity?.slug);
  const followedCommunities = useSelector(({ context }) => context.currentUser?.followedCommunities);
  const isCommunityMember = useIsCommunityMember();

  useUpdateEffect(() => {
    if (!id || !slug) return;
    if (isCommunityMember) {
      dispatch(fetchPrivatePost(id, slug));
    } else {
      dispatch(fetchPost(id, slug));
    }
  }, [followedCommunities]);
};

export const useIsFreePost = (): boolean => {
  const postId = useSelector(({ pages }) => pages.singlePost.post?.id);
  const freePostId = useSelector(({ pages }) => pages.singlePost.freePostId);
  return postId === freePostId;
};

const useSecondPageBlock = () => {
  const dispatch = useDispatch();
  const isFreePost = useIsFreePost();
  const { currentUser } = useSelector(({ context }) => context);
  const { isSecondPageBlockMsgBoxVisible, post } = useSelector(({ pages }) => pages.singlePost);
  const postId = post?.id;

  // On page load, update Redux store if key already exists to send second post block footfall event
  useEffect(() => {
    const isSecondPostBlock = Boolean(getLocalStorageItem(Key.secondPageBlock));
    if (!currentUser && isSecondPostBlock) {
      dispatch(showSecondPageBlockMessageBox());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Client side navigation
  useEffect(() => {
    if (!isFreePost && !currentUser) {
      dispatch(showSecondPageBlockMessageBox());
      setLocalStorageItem(Key.secondPageBlock, 'true');
    }
  }, [currentUser, dispatch, isFreePost]);

  // Send Footfall event every time the visitor sees the second post block
  useEffect(() => {
    if (!postId) return;
    if (isSecondPageBlockMsgBoxVisible) {
      trackFootfallEvent(Event.CLICKED, { clickType: 'sign-up', clickMetadata: { section: Section.SecondPost } });
    }
  }, [isSecondPageBlockMsgBoxVisible, postId]);
};
